import { useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import { useFormik } from 'formik';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { useGetAllKTreeDefinitionList } from 'services/v1/SystemTenant/AROKMS/KTreeBuilderService';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import Autocomplete from '@mui/material/Autocomplete';
import { RULES_CONSTANT } from 'constant/RuleConstant';
const ModalContentStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  height: 340,
  overflow: 'auto',
  borderRadius: 1,
  py: 2,
};

const textInputStyles = {
  width: '100%',
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export interface ModalProcedureFieldRuleParamsState {
  open: boolean;
  ruleTypeCode: string;
  initialValue: ModalProcedureFieldFormValues;
  tableDefinitionId: string;
  fieldName: string;
}

export interface ModalProcedureFieldRuleParamsProps {
  visible?: boolean;
  onClose?: () => void;
  onSubmitParameter?: (data: { params: ModalProcedureFieldFormValues; tableDefinitionId: string }) => void;
  referenceFieldNames?: string[];
  ruleTypeCode: string;
  tableDefinitionId: string;
  initialValue?: ModalProcedureFieldFormValues;
  fieldName: string;
}

export type ModalProcedureFieldFormValues = {
  parameter: string;
  ktreeDefinition: AutoCompleteItem | null;
};

export function ModalProcedureFieldRuleParams(props: ModalProcedureFieldRuleParamsProps) {
  const {
    onClose,
    visible = false,
    onSubmitParameter,
    ruleTypeCode,
    tableDefinitionId,
    initialValue,
    fieldName,
  } = props;

  const { data: ktreeDefintionDropdown } = useGetAllKTreeDefinitionList();

  const handleOnCancel = () => {
    handleOnCloseModal?.();
  };

  const handleOnCloseModal = () => {
    onClose?.();
  };

  const handleOnSave = (data: ModalProcedureFieldFormValues) => {
    onSubmitParameter?.({
      params: data,
      tableDefinitionId,
    });
    handleOnCloseModal();
  };

  const validationSchema = useMemo(() => {
    return Yup.object({
      parameter: Yup.string().nullable(),
      ktreeDefinition: Yup.object().nullable(),
    });
  }, []);

  const formik = useFormik<ModalProcedureFieldFormValues>({
    initialValues: props.initialValue || { parameter: '', ktreeDefinition: null },
    validationSchema: validationSchema,
    onSubmit: handleOnSave,
  });

  const ktreeOptions = useMemo(() => {
    return ktreeDefintionDropdown?.data.sort((a, b) => a.label!.localeCompare(b.label!)) ?? [];
  }, [ktreeDefintionDropdown?.data]);

  useEffect(() => {
    if (initialValue) {
      formik.setValues(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, tableDefinitionId]);

  return (
    <div>
      <Modal open={visible} onClose={handleOnCloseModal}>
        <Stack direction='column' sx={ModalContentStyle} justifyContent='space-between'>
          <Stack>
            <Stack px={2} justifyContent='space-between' direction='row' alignItems='center'>
              <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                Rule Parameters for {fieldName}
              </Typography>
              <IconButton onClick={handleOnCloseModal} component='label'>
                <HighlightOffRoundedIcon sx={closeIconStyle} />
              </IconButton>
            </Stack>
            <Stack
              sx={{
                bgcolor: '#FBFBFB',
                px: 2,
                borderTop: '1px solid #E3EBF6',
              }}
            ></Stack>
          </Stack>

          <Stack px={2} pt={2}>
            {ruleTypeCode === RULES_CONSTANT.TEXT.RULE_CONCATENATE_ATTRIBUTES && (
              <TextField
                value={formik.values.parameter}
                name='parameter'
                onChange={(e) => formik.setFieldValue('parameter', e.target.value)}
                onBlur={formik.handleBlur}
                label='Concatenation Rule'
                sx={textInputStyles}
                size='small'
                placeholder='Input concatenation fields...'
                type='text'
                error={formik.touched.parameter && Boolean(formik.errors.parameter)}
                helperText={formik.touched.parameter && formik.errors.parameter}
              />
            )}

            {ruleTypeCode === RULES_CONSTANT.TEXT.RULE_SELECT_FROM_KTREE_TABLE && (
              <Autocomplete
                options={ktreeOptions}
                getOptionLabel={(option) => option.label || ''}
                value={formik.values.ktreeDefinition}
                onChange={(_, value) => formik.setFieldValue('ktreeDefinition', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name='ktreeDefinition'
                    label='Select KTree Definition'
                    placeholder='Select KTree Definition...'
                    error={formik.touched.ktreeDefinition && Boolean(formik.errors.ktreeDefinition)}
                    helperText={formik.touched.ktreeDefinition && (formik.errors.ktreeDefinition as string)}
                  />
                )}
              />
            )}
          </Stack>

          <Stack px={2}>
            <Divider sx={{ mb: 1, mt: 2 }} />
            <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
              <Button variant='main-table-panel-border' onClick={handleOnCancel}>
                Cancel
              </Button>
              <ButtonLoading variant='main-table-panel' onClick={() => formik.handleSubmit()}>
                Save Parameter
              </ButtonLoading>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    </div>
  );
}
